import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { Page } from '../../types';
import { storage, db } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, addDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import RichTextEditor from '../../components/RichTextEditor';
import { useToast } from '../../hooks/useToast';

const generateSlug = (title: string) => title.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');

const UploadIcon: React.FC<{className?: string}> = ({className}) => <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>;

const ImageUploader: React.FC<{ label: string; value: string; onChange: (value: string) => void; }> = ({ label, value, onChange }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = useState(false);
    const { showToast } = useToast();

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setIsUploading(true);
            try {
                const storageRef = ref(storage, `pages/${file.name}_${Date.now()}`);
                await uploadBytes(storageRef, file);
                const downloadURL = await getDownloadURL(storageRef);
                onChange(downloadURL);
                showToast('Image uploaded successfully.', { type: 'success' });
            } catch (error: any) {
                showToast(error.code === 'storage/unauthorized' ? 'Permission Denied: Check Firebase Storage rules.' : 'Upload failed.', { type: 'error' });
            } finally {
                setIsUploading(false);
            }
        }
    };
    
    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            <div className="relative w-full h-48 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center cursor-pointer hover:border-gray-500 transition-colors bg-gray-50" onClick={() => !isUploading && inputRef.current?.click()}>
                 {isUploading && (<div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-md z-10"><div className="w-8 h-8 border-2 border-dashed rounded-full animate-spin border-gray-800"></div></div>)}
                {value ? (<img src={value} alt={label} className="max-h-full max-w-full object-contain p-1" />) : (<div className="text-center text-gray-500"><UploadIcon className="mx-auto h-8 w-8"/><span className="mt-2 text-sm">Click to upload</span></div>)}
            </div>
            <input type="file" ref={inputRef} className="hidden" accept="image/png, image/jpeg, image/gif, image/webp" onChange={handleFileChange} disabled={isUploading}/>
        </div>
    );
};

const AdminPageFormPage: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditing = Boolean(id);
    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(isEditing);
    const [isSaving, setIsSaving] = useState(false);
    
    const [page, setPage] = useState<Omit<Page, 'id'>>({
        title: '',
        slug: '',
        content: '',
        published: true,
        order: 0,
        layoutTemplate: 'standard',
        featureImageUrl: '',
        seoTitle: '',
        seoDescription: '',
    });

    useEffect(() => {
        if (isEditing && id) {
            const fetchPage = async () => {
                const docRef = doc(db, 'pages', id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setPage(docSnap.data() as Page);
                } else {
                    showToast('Page not found.', { type: 'error' });
                    navigate('/admin/pages');
                }
                setIsLoading(false);
            }
            fetchPage();
        }
    }, [id, isEditing, navigate, showToast]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        if (type === 'checkbox') {
            setPage(prev => ({...prev, [name]: (e.target as HTMLInputElement).checked }));
        } else {
            setPage(prev => ({ ...prev, [name]: value }));
            if (name === 'title') {
                setPage(prev => ({...prev, slug: generateSlug(value), seoTitle: value }));
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            if (isEditing && id) {
                await updateDoc(doc(db, 'pages', id), page);
                showToast('Page updated successfully!', { type: 'success' });
            } else {
                const pagesSnapshot = await getDocs(collection(db, 'pages'));
                const newOrder = pagesSnapshot.size;
                await addDoc(collection(db, 'pages'), { ...page, order: newOrder });
                showToast('Page created successfully!', { type: 'success' });
            }
            navigate('/admin/pages');
        } catch (error) {
            showToast('Failed to save page.', { type: 'error' });
            setIsSaving(false);
        }
    };
    
    if (isLoading) {
        return <div className="text-center p-8">Loading page details...</div>;
    }

    const showFeatureImageUploader = page.layoutTemplate === 'two-column' || page.layoutTemplate === 'hero';

    return (
        <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-6">{isEditing ? 'Edit Page' : 'Add New Page'}</h2>
            <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 space-y-6 bg-white p-8 rounded-lg shadow-md h-fit">
                        <div>
                            <label htmlFor="title" className="block text-sm font-medium text-gray-700">Page Title</label>
                            <input type="text" name="title" id="title" value={page.title} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"/>
                        </div>
                         <div>
                            <label htmlFor="slug" className="block text-sm font-medium text-gray-700">URL Slug</label>
                            <input type="text" name="slug" id="slug" value={page.slug} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500 bg-gray-50"/>
                            <p className="mt-1 text-xs text-gray-500 italic">Page URL will be: /page/{page.slug}</p>
                        </div>
                        <div>
                            <label htmlFor="content" className="block text-sm font-medium text-gray-700">Page Content</label>
                            <RichTextEditor value={page.content} onChange={(value) => setPage(prev => ({...prev, content: value}))}/>
                        </div>
                    </div>
                    <div className="space-y-6 bg-white p-8 rounded-lg shadow-md h-fit">
                        <div>
                            <label htmlFor="layoutTemplate" className="block text-sm font-medium text-gray-700">Layout Template</label>
                            <select name="layoutTemplate" id="layoutTemplate" value={page.layoutTemplate} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500">
                                <option value="standard">Standard</option>
                                <option value="two-column">Two-Column with Image</option>
                                <option value="hero">Full-Width Hero</option>
                                <option value="contact-split">Contact (Split with Map)</option>
                            </select>
                        </div>
                         {showFeatureImageUploader && (
                            <ImageUploader label="Feature Image" value={page.featureImageUrl || ''} onChange={(val) => setPage(p => ({...p, featureImageUrl: val}))}/>
                         )}
                         <div className="flex items-start">
                           <div className="flex items-center h-5">
                             <input id="published" name="published" type="checkbox" checked={page.published} onChange={handleChange} className="h-4 w-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500" />
                           </div>
                           <div className="ml-3 text-sm">
                             <label htmlFor="published" className="font-medium text-gray-700">Published</label>
                             <p className="text-gray-500">Uncheck to save as a draft. It will not appear in the navigation.</p>
                           </div>
                        </div>
                        <div className="border-t pt-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-2">SEO Settings</h3>
                            <div>
                                <label htmlFor="seoTitle" className="block text-sm font-medium text-gray-700">SEO Title</label>
                                <input type="text" name="seoTitle" id="seoTitle" value={page.seoTitle} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"/>
                                <p className="mt-1 text-xs text-gray-500">{page.seoTitle?.length || 0} / 60 characters recommended</p>
                            </div>
                            <div className="mt-4">
                                <label htmlFor="seoDescription" className="block text-sm font-medium text-gray-700">Meta Description</label>
                                <textarea name="seoDescription" id="seoDescription" rows={4} value={page.seoDescription} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"></textarea>
                                <p className="mt-1 text-xs text-gray-500">{page.seoDescription?.length || 0} / 160 characters recommended</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end space-x-4 bg-white p-4 rounded-lg shadow-md sticky bottom-4">
                    <button type="button" onClick={() => navigate('/admin/pages')} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancel</button>
                    <button type="submit" disabled={isSaving} className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400">
                        {isSaving ? 'Saving...' : 'Save Page'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AdminPageFormPage;